"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Check, Calendar } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
interface PricingPlan {
  duration: string;
  title: string;
  price: string;
  currency: string;
  subtitle: string;
  description: string;
  popular?: boolean;
  features: string[];
}
const pricingPlans: PricingPlan[] = [{
  duration: "30 Min.",
  title: "Kennenlerngespräch",
  price: "30",
  currency: "CHF",
  subtitle: "pro Einzelsitzung",
  description: "Sanfter Einstieg zur Erfassung deiner Themen und energetischen Ausrichtung.",
  features: ["Standortbestimmung der Chakren", "Sanfte energetische Zentrierung", "Impuls für den Alltag", "Online oder vor Ort"]
}, {
  duration: "90 Min.",
  title: "Ganzheitliche Chakra-Begleitung",
  price: "180",
  currency: "CHF",
  subtitle: "pro Einzelsitzung",
  popular: true,
  description: "Tiefgehende Session zur gezielten Klärung, Harmonisierung und Stärkung deiner Energiezentren.",
  features: ["Chakra-Analyse", "Chakra-Reinigung und -Heilung", "Erdung", "Integrationsübung für zu Hause"]
}, {
  duration: "90 Min.",
  title: "Intensiv-Paket Chakra Heilung",
  price: "850",
  currency: "CHF",
  subtitle: "5 Einzelsitzungen",
  description: "Ganzheitlicher Entwicklungsraum für tiefgehende Klärung und nachhaltige emotionale Neuausrichtung.",
  features: ["Vertiefte Prozessarbeit", "Tiefenmeditation", "Ressourcenstärkung", "Nachbetreuung: 1 telefonisches Gespräch"]
}];
export default function PreiseTermine() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="preisetermine" data-nav="light" className="py-16 md:py-24 bg-background text-foreground relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-14 md:mb-16">
          <Badge variant="secondary" className="mb-3 px-3 py-1 bg-secondary text-secondary-foreground rounded-md text-xs tracking-wide font-normal">
            Investition in dein Wohlbefinden
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-[40px] font-serif font-bold text-foreground tracking-tight mb-4">
            Preise und Terminvereinbarungen
          </h2>
          <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">Transparente Preise für deine persönliche Chakra-Heilung in Zürich. Wähle die passende Dauer für dein Anliegen und frage direkt deinen Wunschtermin an.</p>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 items-stretch">
          {pricingPlans.map((plan, i) => <motion.div key={plan.title} variants={itemVariants} data-index={i} className="flex">
              <Card className={`flex flex-col justify-between w-full relative border transition-all rounded-xl ${plan.popular ? "border-primary/40 bg-card shadow-md ring-1 ring-primary/20" : "border-border bg-card shadow-xs hover:border-border/80"}`}>
                <div>
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-semibold uppercase tracking-wider text-primary">
                            {plan.duration}
                          </span>
                        </div>
                        <CardTitle className="text-lg font-serif font-bold text-card-foreground">
                          {plan.title}
                        </CardTitle>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="flex items-baseline justify-end gap-1">
                          <span className="text-2xl font-bold font-serif text-foreground">
                            {plan.price}
                          </span>
                          <span className="text-xs font-medium text-muted-foreground">
                            {plan.currency}
                          </span>
                        </div>
                        <span className="text-[11px] text-muted-foreground">{plan.subtitle}</span>
                      </div>
                    </div>
                    <CardDescription className="text-xs text-muted-foreground mt-1">
                      {plan.description}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="pt-0 pb-6">
                    <ul className="space-y-2 text-xs text-foreground/85 border-t border-border/60 pt-3">
                      {plan.features.map((feat, featIdx) => <li key={featIdx} className="flex items-center gap-2">
                          <Check className="w-3.5 h-3.5 text-primary shrink-0" />
                          <span>{feat}</span>
                        </li>)}
                    </ul>
                  </CardContent>
                </div>
              </Card>
            </motion.div>)}
        </motion.div>

        <div className="mt-10 md:mt-12 flex justify-center">
          <Button asChild size="lg" className="shadow-xs gap-2">
            <Link to="/ContactPage">
              <SafeHydrate><Calendar className="size-4" /></SafeHydrate>
              <span>Termin anfragen</span>
            </Link>
          </Button>
        </div>

        <div className="mt-12 max-w-3xl mx-auto p-5 md:p-6 bg-muted rounded-xl border border-border/70 text-xs sm:text-sm text-muted-foreground space-y-2">
          <p className="font-semibold text-foreground text-sm">Wichtige Hinweise:</p>
          <ul className="list-disc pl-5 space-y-1.5 leading-relaxed">
            <li>Die Kosten werden nicht von der Krankenkasse übernommen.</li>
            <li>Ich ersetze keinen Arzt.</li>
            <li>Termine können bis 24 Stunden im Voraus kostenfrei abgesagt werden.</li>
            <li>Bezahlung bequem per TWINT oder bar vor Ort.</li>
          </ul>
        </div>
      </div>
    </section>;
}